// Client-side helper for triggering the enrich-region-brands edge
// function. The edge function processes canonicals in time-bounded
// chunks (~40s budget per invocation, ~5 concurrent Haiku calls per
// wave) and returns when its budget is up. For a brand-new region
// covering all ~640 canonicals, that means ~10-15 invocations end-
// to-end. The trigger handles the re-fire loop so the caller just
// fires once and the system grinds through to completion in the
// background.
//
// All calls are fire-and-forget — the user shouldn't block on a
// 10-minute Haiku pass. The status surfaces via the
// useRegionalEnrichmentStatus hook + banner UI, and finished
// brands appear in the picker on the next session reload (or sooner
// once we wire ingredient_info realtime).

import { supabase } from "./supabase";
import { findMetro } from "../data/usMetros";

// Re-fire interval — how long to wait after one invocation returns
// before kicking off the next one. The edge function may finish
// early if `pending` empties; we always poll status after firing
// to detect that and break the loop.
const RE_FIRE_DELAY_MS = 1500;
// Hard cap so a runaway / stuck region can't burn through Haiku
// indefinitely. With ~640 canonicals at 5 concurrent × 40s budget
// per invocation, full enrichment fits in ~15 invocations. 30 is
// a safety buffer.
const MAX_INVOCATIONS_PER_REGION = 30;
// How long to wait when the edge function returns skipped:in_progress
// (another tab/user holds the lock). Must exceed the function's
// STALE_LOCK_MS so the next retry can reclaim if the prior holder
// died. Edge function uses 5min; we wait 5.5min.
const STALE_LOCK_WAIT_MS = 5.5 * 60 * 1000;

async function invokeOnce(regionKey, displayName) {
  console.info("[enrich-region-brands] invoking edge fn:", regionKey);
  const { data, error } = await supabase.functions.invoke("enrich-region-brands", {
    body: { region_key: regionKey, display_name: displayName },
  });
  if (error) {
    // Log the FULL error object — message alone often hides the
    // actual cause (RLS reject, missing JWT, missing API key on the
    // server side, etc.). Status + context are where the real
    // signal lives.
    console.warn("[enrich-region-brands] invoke err:", {
      regionKey, message: error.message, status: error.status, context: error.context,
    });
    return null;
  }
  console.info("[enrich-region-brands] invoke result:", regionKey, data);
  return data || null;
}

async function loopUntilDone(regionKey, displayName) {
  let invocations = 0;
  while (invocations < MAX_INVOCATIONS_PER_REGION) {
    const result = await invokeOnce(regionKey, displayName);
    invocations++;
    // result is { status, processed, total, completed, remaining, self_fired, ... }
    // status === "done"        → all canonicals processed
    // self_fired === true      → server scheduled its own next wave
    //                            via EdgeRuntime.waitUntil; client
    //                            doesn't need to keep firing. The
    //                            run continues regardless of tab
    //                            state from here forward.
    // status === "in_progress" → more pending; only re-fire when
    //                            server didn't self-fire (legacy
    //                            edge-fn deploy, or self-fire failed)
    // status === "skipped" + reason="recent"      → done within TTL, bail
    // status === "skipped" + reason="in_progress" → another worker holds
    //                                               a fresh lock; wait
    //                                               out STALE_LOCK_MS
    //                                               and retry
    // null / error → bail (don't infinite-loop on a broken function)
    if (!result) break;
    if (result.status === "done" || result.remaining === 0) break;
    if (result.self_fired === true) {
      console.info("[enrich-region-brands] server self-perpetuating; client loop done");
      break;
    }
    if (result.status === "skipped") {
      if (result.reason === "recent") {
        console.info("[enrich-region-brands] region recently completed — stopping loop");
        break;
      }
      // reason === "in_progress" — another worker holds the lock.
      // Used to bail here, which meant a single dropped invocation
      // (tab close, network blip, transient skip) would permanently
      // strand the run at whatever its partial-progress count was —
      // produced the "stopped at 40" stuck state for Springfield.
      console.info(
        "[enrich-region-brands] another worker holds lock; waiting",
        STALE_LOCK_WAIT_MS, "ms before retry"
      );
      await new Promise((r) => setTimeout(r, STALE_LOCK_WAIT_MS));
      continue;
    }
    // Brief pause between waves so the edge function has a chance
    // to finish writing state and the regional_brand_enrichment
    // status row updates cleanly.
    await new Promise((r) => setTimeout(r, RE_FIRE_DELAY_MS));
  }
}

/**
 * triggerRegionEnrichment(regionKeys)
 *
 *   regionKeys — string[] of MSA keys to enrich. Each fires its own
 *                edge function call. Empty / null inputs no-op.
 *
 * Returns a promise that resolves when all calls have BEEN SENT
 * (not when they complete). Callers shouldn't await; this is
 * fire-and-forget.
 */
// In-flight set — protects against firing the SAME region's loop
// twice in parallel (e.g. user mashes Re-fire, or onboarding +
// Settings auto-fire collide). Each parallel loop hammers the edge
// fn AND the DB; one's enough. Cleared inside loopUntilDone's
// finally so the region can be re-fired again later.
const inFlight = new Set();

export async function triggerRegionEnrichment(regionKeys) {
  if (!Array.isArray(regionKeys) || regionKeys.length === 0) {
    console.info("[enrich-region-brands] trigger skipped — no region keys");
    return;
  }
  console.info("[enrich-region-brands] trigger fired for:", regionKeys);
  for (const key of regionKeys) {
    const metro = findMetro(key);
    if (!metro) {
      console.warn("[enrich-region-brands] unknown metro key:", key);
      continue;
    }
    if (inFlight.has(metro.key)) {
      console.info("[enrich-region-brands] already running, skipping:", metro.key);
      continue;
    }
    console.info("[enrich-region-brands] kicking off loop:", metro.key, metro.label);
    inFlight.add(metro.key);
    // Don't await — let each region's loop run independently in the
    // background. Errors get logged inside loopUntilDone. The finally
    // releases the in-flight guard so the region can be re-triggered
    // later (manual re-fire, daily refresh, etc).
    loopUntilDone(metro.key, metro.label)
      .catch((e) => {
        console.warn("[enrich-region-brands] loop failed:", key, e?.message || e);
      })
      .finally(() => {
        inFlight.delete(metro.key);
      });
  }
}

/**
 * Diff two shopping-cities arrays and trigger enrichment for any
 * NEWLY-added cities. Used by the profile-save callback so users
 * who add Springfield, MO + Branson, MO at once each fire one
 * enrichment loop.
 */
export function triggerEnrichmentForNewCities(prev, next) {
  const before = new Set(Array.isArray(prev) ? prev : []);
  const after  = Array.isArray(next) ? next : [];
  const added  = after.filter((k) => !before.has(k));
  if (added.length === 0) return;
  triggerRegionEnrichment(added);
}
